import React, { useState } from 'react';
import {
  Check, Clock, Copy, Crown, Eye, Link2, LogOut, Mail, MoreHorizontal, Power, RefreshCw, ShieldCheck, Trash2, UserPlus, Users, X,
} from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { ago, date, dateTime, daysUntil } from '../lib/format';
import { useAccess } from '../context/AuthContext';
import {
  Button, Card, Chip, Empty, ErrorBanner, Field, Input, Modal, PageHeader, PageSkeleton, Stat, Tone, useToast,
} from '../components/ui';

type Level = 'owner' | 'admin' | 'viewer';
interface Member {
  id: string; name: string; email: string; is_active: boolean; level: Level; created_at: string;
  last_sign_in: string | null; active_sessions: number; has_pin: boolean; is_me: boolean;
}
interface Invite { id: string; email: string; name: string | null; level: Level; invited_by_email: string | null; created_at: string; expires_at: string; expired: boolean }
interface TeamResp { me: { id: string; level: Level }; members: Member[]; invites: Invite[] }

export const LEVELS: { key: Level; label: string; tone: Tone; icon: React.ElementType; text: string }[] = [
  { key: 'owner', label: 'Owner', tone: 'purple', icon: Crown, text: 'Everything, including managing this team.' },
  { key: 'admin', label: 'Admin', tone: 'blue', icon: ShieldCheck, text: 'Manage tenants, plans, users and notifications. Can’t manage the team.' },
  { key: 'viewer', label: 'Read-only', tone: 'slate', icon: Eye, text: 'See everything, change nothing. For support and finance.' },
];
const LEVEL = Object.fromEntries(LEVELS.map(l => [l.key, l])) as Record<Level, typeof LEVELS[number]>;

function Avatar({ name, email, muted }: { name: string; email: string; muted?: boolean }) {
  return (
    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${muted ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 text-white'}`}>
      {(name || email || '?').charAt(0).toUpperCase()}
    </span>
  );
}

function LevelPicker({ value, onChange }: { value: Level; onChange: (l: Level) => void }) {
  return (
    <div className="space-y-2">
      {LEVELS.map(l => (
        <button key={l.key} type="button" onClick={() => onChange(l.key)}
          className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition ${value === l.key ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
          <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${value === l.key ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
            {value === l.key && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
          </span>
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900"><l.icon className="h-3.5 w-3.5 text-slate-500" />{l.label}</span>
            <span className="block text-xs text-slate-500">{l.text}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function CopyLink({ url, expiresAt }: { url: string; expiresAt: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* select-and-copy fallback below */ }
    setCopied(true); window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1.5 pl-3">
        <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
        <input readOnly value={url} onFocus={e => e.target.select()} className="min-w-0 flex-1 bg-transparent font-mono text-xs text-slate-700 focus:outline-none" />
        <Button size="sm" variant={copied ? 'secondary' : 'primary'} icon={copied ? Check : Copy} onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5" /> Works once, until {dateTime(expiresAt)}. It won't be shown again — make a new one if it's lost.
      </p>
    </div>
  );
}

export default function Team() {
  const toast = useToast();
  const { isOwner } = useAccess();
  const { data, error, loading, reload } = useApi<TeamResp>('/team');
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<{ email: string; url: string; expires_at: string } | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [changing, setChanging] = useState<Member | null>(null);
  const [confirm, setConfirm] = useState<{ member: Member; action: 'deactivate' | 'signout' | 'delete' } | null>(null);

  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorBanner text={error || 'Could not load the team'} onRetry={reload} />;

  const active = data.members.filter(m => m.is_active);
  const owners = active.filter(m => m.level === 'owner').length;
  const pending = data.invites.filter(i => !i.expired);

  const run = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); toast('ok', ok); reload(); } catch (e) { toast('error', errorText(e)); }
  };
  const renew = (inv: Invite) => run(async () => {
    const { data: d } = await api.post(`/team/invites/${inv.id}/renew`);
    setLink({ email: inv.email, url: d.invite_url, expires_at: d.expires_at });
  }, 'New invite link created');

  return (
    <div>
      <PageHeader title="Team" subtitle="People at FluxWatch who can sign in to this admin console"
        actions={isOwner ? <Button variant="primary" icon={UserPlus} onClick={() => setInviting(true)}>Invite member</Button> : undefined} />

      {!isOwner && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          <ShieldCheck className="h-4 w-4 text-slate-400" /> Only owners can invite people or change access.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Active members" value={active.length} icon={Users} tone="blue" sub={`${data.members.length - active.length} deactivated`} />
        <Stat label="Owners" value={owners} icon={Crown} tone="purple" sub={owners === 1 ? 'Add a second owner as a backup' : 'Can manage this team'} />
        <Stat label="Pending invites" value={pending.length} icon={Mail} tone={pending.length ? 'amber' : 'slate'} sub={`${data.invites.length - pending.length} expired`} />
        <Stat label="Signed in now" value={active.filter(m => m.active_sessions > 0).length} icon={ShieldCheck} tone="green" sub="With an active session" />
      </div>

      <Card className="mt-5" title="Members" icon={Users} tone="blue" bodyClass="p-0">
        <table className="min-w-full">
          <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
            {['Member', 'Access', 'Status', 'Last sign-in', 'Joined', ''].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.members.map(m => {
              const l = LEVEL[m.level];
              return (
                <tr key={m.id} className={m.is_active ? '' : 'bg-slate-50/50'}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={m.name} email={m.email} muted={!m.is_active} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">{m.name || m.email}{m.is_me && <Chip tone="slate">You</Chip>}</div>
                        <div className="truncate text-xs text-slate-500">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Chip tone={l.tone}><l.icon className="h-3 w-3" />{l.label}</Chip></td>
                  <td className="px-4 py-3">
                    {!m.is_active ? <Chip tone="slate" dot>Deactivated</Chip>
                      : m.active_sessions > 0 ? <Chip tone="green" dot>Signed in</Chip> : <Chip tone="blue" dot>Active</Chip>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(m.last_sign_in)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{date(m.created_at)}</td>
                  <td className="relative px-4 py-3 text-right">
                    {isOwner && !m.is_me && (
                      <>
                        <button onClick={() => setMenu(menu === m.id ? null : m.id)} aria-label="Member actions"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><MoreHorizontal className="h-4 w-4" /></button>
                        {menu === m.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setMenu(null)} />
                            <div className="absolute right-4 top-11 z-20 w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-lg">
                              {m.is_active && <MenuItem icon={ShieldCheck} onClick={() => { setMenu(null); setChanging(m); }}>Change access</MenuItem>}
                              {m.is_active && m.active_sessions > 0 && <MenuItem icon={LogOut} onClick={() => { setMenu(null); setConfirm({ member: m, action: 'signout' }); }}>Sign out everywhere</MenuItem>}
                              {m.is_active
                                ? <MenuItem icon={Power} danger onClick={() => { setMenu(null); setConfirm({ member: m, action: 'deactivate' }); }}>Deactivate</MenuItem>
                                : <MenuItem icon={Power} onClick={() => { setMenu(null); run(() => api.post(`/team/members/${m.id}/status`, { active: true }), `${m.name || m.email} can sign in again`); }}>Reactivate</MenuItem>}
                              <div className="my-1 border-t border-slate-100" />
                              <MenuItem icon={Trash2} danger onClick={() => { setMenu(null); setConfirm({ member: m, action: 'delete' }); }}>Delete permanently</MenuItem>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="mt-5" title="Pending invites" icon={Mail} tone="amber" bodyClass="p-0">
        {data.invites.length === 0 ? (
          <Empty icon={Mail} title="No pending invites" text={isOwner ? 'Invite someone and send them the link. They choose their own password and PIN.' : undefined} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.invites.map(i => {
              const days = daysUntil(i.expires_at);
              return (
                <li key={i.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <Avatar name={i.name || ''} email={i.email} muted />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900">{i.name || i.email}</div>
                    <div className="text-xs text-slate-500">{i.name ? `${i.email} · ` : ''}invited by {i.invited_by_email || 'unknown'} {ago(i.created_at)}</div>
                  </div>
                  <Chip tone={LEVEL[i.level].tone}>{LEVEL[i.level].label}</Chip>
                  {i.expired ? <Chip tone="red">Expired</Chip> : <Chip tone="amber">Expires {days !== null && days <= 1 ? 'today' : `in ${days} days`}</Chip>}
                  {isOwner && (
                    <div className="flex gap-1.5">
                      <Button size="sm" icon={RefreshCw} onClick={() => renew(i)}>New link</Button>
                      <Button size="sm" variant="ghost" icon={X} onClick={() => run(() => api.delete(`/team/invites/${i.id}`), `Invite for ${i.email} revoked`)}>Revoke</Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card className="mt-5" title="Access levels" icon={ShieldCheck} tone="slate">
        <div className="grid gap-3 md:grid-cols-3">
          {LEVELS.map(l => (
            <div key={l.key} className="rounded-lg border border-slate-100 p-3">
              <Chip tone={l.tone}><l.icon className="h-3 w-3" />{l.label}</Chip>
              <p className="mt-2 text-sm text-slate-600">{l.text}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">Every member signs in with a password and a 6-digit PIN. There must always be at least one active owner.</p>
      </Card>

      {inviting && <InviteModal onClose={() => setInviting(false)} onCreated={(email, url, expires_at) => { setInviting(false); setLink({ email, url, expires_at }); reload(); }} />}

      <Modal open={!!link} title="Send this invite link" onClose={() => setLink(null)} footer={<Button variant="primary" onClick={() => setLink(null)}>Done</Button>}>
        {link && (
          <>
            <p className="mb-3 text-sm text-slate-600">Send this link to <b className="font-medium text-slate-900">{link.email}</b> (Slack, email, however you like). They'll set their own password and PIN.</p>
            <CopyLink url={link.url} expiresAt={link.expires_at} />
          </>
        )}
      </Modal>

      {changing && <ChangeLevelModal member={changing} onClose={() => setChanging(null)} onSaved={() => { setChanging(null); reload(); }} />}

      <Modal open={!!confirm} onClose={() => setConfirm(null)}
        title={confirm?.action === 'deactivate' ? `Deactivate ${confirm.member.name || confirm.member.email}?`
          : confirm?.action === 'delete' ? `Delete ${confirm.member.name || confirm.member.email} permanently?`
          : `Sign ${confirm?.member.name || confirm?.member.email} out?`}
        footer={<><Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant={confirm?.action === 'signout' ? 'primary' : 'danger'} onClick={() => {
            const c = confirm!; setConfirm(null);
            const who = c.member.name || c.member.email;
            if (c.action === 'deactivate') run(() => api.post(`/team/members/${c.member.id}/status`, { active: false }), `${who} was deactivated`);
            else if (c.action === 'delete') run(() => api.delete(`/team/members/${c.member.id}`), `${who} was removed from the team`);
            else run(() => api.post(`/team/members/${c.member.id}/sign-out`), `${who} was signed out`);
          }}>{confirm?.action === 'deactivate' ? 'Deactivate' : confirm?.action === 'delete' ? 'Delete permanently' : 'Sign out'}</Button></>}>
        <p className="text-sm text-slate-600">
          {confirm?.action === 'deactivate'
            ? 'They’re signed out straight away and can’t sign in until you reactivate them. Their past actions stay in the audit log.'
            : confirm?.action === 'delete'
            ? <>Their account is removed for good and they’re signed out everywhere. This can’t be undone, but you can invite <b className="font-medium text-slate-900">{confirm.member.email}</b> again later. Their past actions stay in the audit log.</>
            : 'Every browser they’re signed in on is signed out. They can sign in again with their password and PIN.'}
        </p>
      </Modal>
    </div>
  );
}

function MenuItem({ icon: Icon, children, onClick, danger }: { icon: React.ElementType; children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button onClick={onClick} className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-50'}`}>
      <Icon className="h-4 w-4" />{children}
    </button>
  );
}

function InviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, url: string, expires_at: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [level, setLevel] = useState<Level>('admin');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) { setErr('Enter a valid email address'); return; }
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/team/invites', { email: email.trim(), name: name.trim() || null, level });
      onCreated(email.trim().toLowerCase(), data.invite_url, data.expires_at);
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Invite a team member" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon={UserPlus} loading={busy} onClick={submit}>Create invite link</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Work email"><Input type="email" autoFocus value={email} onChange={e => { setEmail(e.target.value); setErr(null); }} placeholder="priya@fluxvell.com" /></Field>
          <Field label="Name (optional)"><Input value={name} onChange={e => setName(e.target.value)} placeholder="Priya Sharma" /></Field>
        </div>
        <div>
          <span className="mb-1.5 block text-xs font-medium text-slate-600">Access</span>
          <LevelPicker value={level} onChange={setLevel} />
        </div>
        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <p className="text-xs text-slate-500">You'll get a one-time link to send them. It expires in 72 hours.</p>
      </div>
    </Modal>
  );
}

function ChangeLevelModal({ member, onClose, onSaved }: { member: Member; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [level, setLevel] = useState<Level>(member.level);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try { await api.patch(`/team/members/${member.id}`, { level }); toast('ok', `${member.name || member.email} is now ${LEVEL[level].label}`); onSaved(); }
    catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open title={`Change access for ${member.name || member.email}`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} disabled={level === member.level} onClick={save}>Save</Button></>}>
      <LevelPicker value={level} onChange={setLevel} />
      {err && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <p className="mt-3 text-xs text-slate-500">Takes effect on their next click — no need to sign them out.</p>
    </Modal>
  );
}
